import { useAtom } from '@reatom/npm-react'
import BigNumber from 'bignumber.js'
import { match } from 'ts-pattern'

import { TransactionAgeCell } from '@root/entities/network/ui/trade-age-cell'
import { SwapDirectionCell, TradeTypeCell } from '@root/entities/trade-type/ui'
import { StringUtil } from '@root/shared'

import { OrdersTableAmountCell } from './orders-table-amount-cell'
import { OrdersTableCurrencyCell } from './orders-table-currency-cell'
import { OrdersTableExpiryCell } from './orders-table-expiry-cell'
import { OrdersTableStatusCell } from './orders-table-status-cell'
import { OrdersTableTriggerCondition } from './orders-table-trigger-condition'
import { OrdersTableWalletCell } from './orders-table-wallet-cell'

import type { OrderTableColumn } from '../atoms'
import type { OrderModel } from '@root/entities/order/atoms'
import type { Column } from '@root/shared/ui-kit'

const AmountCell = ({ value }: { value: OrderModel }) => {
  const { order } = value
  const currency = order.type === 'buy' ? order.pair.base : order.pair.quote

  const [amount] = useAtom((ctx) => {
    return match(value)
      .with({ type: 'limit' }, ({ order }) =>
        order.type === 'buy'
          ? order.purchaseAmountBase
          : order.sellAmountCurrency,
      )
      .with(
        { type: 'market' },
        ({ order }) => ctx.spy(order.swapDetails)?.amountIn ?? BigNumber(0),
      )
      .exhaustive()
  })

  return <OrdersTableAmountCell amount={amount} currency={currency} />
}

const CostCell = ({ value }: { value: OrderModel }) => {
  const [amount] = useAtom((ctx) => {
    return match(value)
      .with({ type: 'limit' }, ({ order }) => {
        if (ctx.spy(order.status) !== 'completed') {
          return BigNumber(0)
        }
        return ctx.spy(order.fulfilledAmount)
      })
      .with({ type: 'market' }, ({ order }) => {
        const swapDetails = ctx.spy(order.swapDetails)
        if (!swapDetails) {
          return BigNumber(0)
        }

        return order.type === 'buy'
          ? swapDetails.amountIn
          : swapDetails.amountOut
      })
      .exhaustive()
  })

  return (
    <OrdersTableAmountCell amount={amount} currency={value.order.pair.base} />
  )
}

const AverageExecutionCell = ({ value }: { value: OrderModel }) => {
  const [averageExecutionMarketCap] = useAtom(
    value.order.averageExecutionBuyMarketCap,
  )

  return (
    <OrdersTableAmountCell amount={averageExecutionMarketCap} currency="$" />
  )
}

export const createOrdersTableColumns = ({
  withCurrency = false,
}: {
  withCurrency?: boolean
}) => {
  const columns: Array<Column<OrderModel, OrderTableColumn> | undefined> = [
    withCurrency
      ? {
          id: 'currency',
          header: ({ t }) => t('token'),
          render: ({ value }) => <OrdersTableCurrencyCell order={value} />,
          getValue: ({ order }) => order.pair.quote.address,
          minWidth: 110,
        }
      : undefined,
    {
      id: 'currency-address',
      getValue: ({ order }) => order.pair.quote.address,
      hidden: true,
    },
    {
      id: 'create-at' as const,
      header: ({ t }) => t('common.age'),
      render: ({ value }) => {
        const styles = {
          '--row-color':
            value.order.type === 'buy' ? 'var(--up-9)' : 'var(--down-9)',
        } as React.CSSProperties

        return (
          <TransactionAgeCell
            style={styles}
            network={value.order.network}
            timestamp={value.order.createdAt}
            transactionHash={undefined}
          />
        )
      },
      width: 80,
      sortable: true,
    },
    {
      id: 'side',
      header: ({ t }) => t('order.table.side'),
      render: ({ value }) => {
        if (value.order.type === 'swap') {
          // TODO: Support swap order types
          return <>-</>
        }
        return <SwapDirectionCell type={value.order.type} />
      },
      width: 60,
    },
    {
      id: 'type',
      header: ({ t }) => t('common.type'),
      render: ({ value: order }) => <TradeTypeCell type={order.orderType} />,
      minWidth: 80,
    },
    {
      id: 'amount',
      header: ({ t }) => t('order.table.order-amt'),
      render: ({ value }) => <AmountCell value={value} />,
      minWidth: 100,
    },
    {
      id: 'cost',
      header: ({ t }) => t('common.cost'),
      render: ({ value }) => <CostCell value={value} />,
      minWidth: 100,
    },
    {
      id: 'avg-exec',
      header: ({ t }) => t('order.table.avg-exec'),
      render: ({ value }) => <AverageExecutionCell value={value} />,
      minWidth: 60,
    },
    {
      id: 'expiry',
      header: ({ t }) => t('order.expiry'),
      render: ({ value }) => {
        if (value.type === 'limit') {
          return <OrdersTableExpiryCell order={value.order} />
        }

        return '-'
      },
      minWidth: 60,
    },
    {
      id: 'wallet',
      header: ({ t }) => StringUtil.capitalize(t('wallet_one')),
      render: ({ value }) => <OrdersTableWalletCell order={value} />,
      minWidth: 120,
    },
    {
      id: 'trigger-condition',
      header: ({ t }) => t('order.trigger-condition'),
      render: ({ value }) => {
        if (value.type === 'limit') {
          return <OrdersTableTriggerCondition order={value.order} />
        }

        return '-'
      },
      minWidth: 140,
    },
    {
      id: 'status',
      header: ({ t }) => t('common.status'),
      render: ({ value }) => <OrdersTableStatusCell order={value} />,
      minWidth: 140,
      justify: 'end',
    },
  ]

  return columns.filter((column) => column !== undefined)
}
